import React, { useState, useEffect, useCallback } from 'react';
import { AdminService } from '../../services/adminService';
import { useAdmin } from '../../hooks/useAdmin';
import LoadingState from '../shared/LoadingState';
import { PDFReportGenerator, DateDetailMode } from '../../utils/pdfReportGenerator';
import { Filter, Download, Calendar, Users, TrendingUp, Search, FileText, MessageSquare, Copy, ArrowUpDown, ArrowUp, ArrowDown, X, CheckCircle2, XCircle } from 'lucide-react';

export default function AdminReports() {
  const { meetingTypes, users } = useAdmin();
  const [activeReport, setActiveReport] = useState<'member-wise' | 'meeting-wise' | 'summary' | 'member-search'>('member-wise');
  const [dateDetailMode, setDateDetailMode] = useState<DateDetailMode>('none');
  const [filters, setFilters] = useState<any>({});
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    // Smart default sorting per report type
    switch (activeReport) {
      case 'meeting-wise':
        setSortColumn('date');
        setSortDirection('desc');
        break;
      case 'member-wise':
        setSortColumn('percentage');
        setSortDirection('asc');
        break;
      case 'summary':
        setSortColumn('percentage');
        setSortDirection('desc');
        break;
      case 'member-search':
        setSortColumn('percentage');
        setSortDirection('asc');
        break;
      default:
        setSortColumn('');
        setSortDirection('asc');
    }
  }, [activeReport]);

  // Build a human-readable summary of active filters
  const getActiveFiltersSummary = useCallback((): string[] => {
    const parts: string[] = [];
    if (filters.meeting_type_id) {
      const mt = meetingTypes.find((t: any) => t.id === filters.meeting_type_id);
      if (mt) parts.push(`Meeting Type: ${mt.name} (${mt.level})`);
    }
    if (filters.start_date) parts.push(`From: ${filters.start_date}`);
    if (filters.end_date) parts.push(`To: ${filters.end_date}`);
    if (filters.convener_id) {
      const conv = users.find((u: any) => u.id === filters.convener_id);
      if (conv) parts.push(`Convener: ${conv.name}`);
    }
    if (searchQuery) parts.push(`Search: "${searchQuery}"`);
    return parts;
  }, [filters, meetingTypes, users, searchQuery]);

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const getSummaryData = useCallback(() => {
    const summaryStats: Record<string, any> = {};

    if (reportData && reportData.length > 0) {
      reportData.forEach((record: any) => {
        const meetingType = record.meeting_instances?.meeting_types?.name || 'Unknown';
        if (!summaryStats[meetingType]) {
          summaryStats[meetingType] = {
            meetingType,
            meetings: new Set(),
            totalMembers: 0,
            totalPresent: 0,
            totalAbsent: 0
          };
        }
        if (record.meeting_instance_id) {
          summaryStats[meetingType].meetings.add(record.meeting_instance_id);
        }
        summaryStats[meetingType].totalMembers++;
        if (record.status === 'Present') summaryStats[meetingType].totalPresent++;
        else summaryStats[meetingType].totalAbsent++;
      });
    }

    const targetMeetingTypes = filters.meeting_type_id
      ? meetingTypes.filter((t: any) => t.id === filters.meeting_type_id)
      : meetingTypes;

    targetMeetingTypes.forEach((mt: any) => {
      if (!summaryStats[mt.name]) {
        summaryStats[mt.name] = {
          meetingType: mt.name,
          meetings: new Set(),
          totalMembers: 0,
          totalPresent: 0,
          totalAbsent: 0
        };
      }
    });

    return Object.values(summaryStats).map((stats: any) => ({
      ...stats,
      totalMeetings: stats.meetings ? stats.meetings.size : 0,
      percentage: stats.totalMembers > 0 ? (stats.totalPresent / stats.totalMembers) * 100 : 0
    }));
  }, [reportData, meetingTypes, filters.meeting_type_id]);

  const getProcessedData = useCallback(() => {
    let processed: any[] = [];

    switch (activeReport) {
      case 'summary': {
        processed = getSummaryData();
        break;
      }

      case 'member-wise': {
        if (!reportData || reportData.length === 0) return [];
        const memberStats = reportData.reduce((acc: any, record: any) => {
          const memberId = record.member_id;
          const memberName = record.members?.name || 'Unknown';
          const memberPhone = record.members?.phone || 'Unknown';
          const meetingType = record.meeting_instances?.meeting_types?.name || 'Unknown';
          const meetingDate = record.meeting_instances?.date || '';
          const key = `${memberId}-${meetingType}`;
          
          if (!acc[key]) {
            acc[key] = {
              name: memberName,
              phone: memberPhone,
              meetingType,
              total: 0,
              present: 0,
              absent: 0,
              presentDates: [],
              absentDates: []
            };
          }
          acc[key].total++;
          if (record.status === 'Present') {
            acc[key].present++;
            if (meetingDate && !acc[key].presentDates.includes(meetingDate)) {
              acc[key].presentDates.push(meetingDate);
            }
          } else {
            acc[key].absent++;
            if (meetingDate && !acc[key].absentDates.includes(meetingDate)) {
              acc[key].absentDates.push(meetingDate);
            }
          }
          return acc;
        }, {});

        processed = Object.values(memberStats).map((stats: any) => ({
          ...stats,
          presentDates: stats.presentDates.sort(),
          absentDates: stats.absentDates.sort(),
          percentage: stats.total > 0 ? (stats.present / stats.total) * 100 : 0
        }));
        break;
      }

      case 'meeting-wise': {
        if (!reportData || reportData.length === 0) return [];
        const meetingStats = reportData.reduce((acc: any, record: any) => {
          const meetingId = record.meeting_instance_id;
          if (!acc[meetingId]) {
            acc[meetingId] = {
              date: record.meeting_instances?.date || '',
              meetingType: record.meeting_instances?.meeting_types?.name || '',
              convener: record.meeting_instances?.users?.name || '',
              total: 0,
              present: 0,
              absent: 0
            };
          }
          acc[meetingId].total++;
          if (record.status === 'Present') acc[meetingId].present++;
          else acc[meetingId].absent++;
          return acc;
        }, {});

        processed = Object.values(meetingStats).map((stats: any) => ({
          ...stats,
          percentage: stats.total > 0 ? (stats.present / stats.total) * 100 : 0
        }));
        break;
      }

      case 'member-search': {
        if (!reportData || reportData.length === 0) return [];
        const searchStats = reportData.reduce((acc: any, record: any) => {
          const memberId = record.member_id;
          const memberName = record.members?.name || 'Unknown';
          const memberPhone = record.members?.phone || 'Unknown';
          const meetingType = record.meeting_instances?.meeting_types?.name || 'Unknown';
          const meetingDate = record.meeting_instances?.date || '';
          
          if (!acc[memberId]) {
            acc[memberId] = {
              name: memberName,
              phone: memberPhone,
              total: 0,
              present: 0,
              absent: 0,
              meetingTypes: new Set(),
              presentDates: [],
              absentDates: []
            };
          }
          acc[memberId].total++;
          if (record.status === 'Present') {
            acc[memberId].present++;
            if (meetingDate && !acc[memberId].presentDates.includes(meetingDate)) {
              acc[memberId].presentDates.push(meetingDate);
            }
          } else {
            acc[memberId].absent++;
            if (meetingDate && !acc[memberId].absentDates.includes(meetingDate)) {
              acc[memberId].absentDates.push(meetingDate);
            }
          }
          acc[memberId].meetingTypes.add(meetingType);
          return acc;
        }, {});

        const filteredStats = Object.values(searchStats)
          .filter((stats: any) => 
            stats.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            stats.phone.includes(searchQuery)
          );

        processed = filteredStats.map((stats: any) => ({
          ...stats,
          meetingTypesStr: Array.from(stats.meetingTypes).join(', '),
          presentDates: stats.presentDates.sort(),
          absentDates: stats.absentDates.sort(),
          percentage: stats.total > 0 ? (stats.present / stats.total) * 100 : 0
        }));
        break;
      }
    }

    if (searchQuery && activeReport !== 'member-search') {
      const q = searchQuery.toLowerCase();
      processed = processed.filter((item: any) => {
        const itemStr = JSON.stringify(item).toLowerCase();
        return itemStr.includes(q);
      });
    }

    if (sortColumn) {
      processed.sort((a, b) => {
        let valA = a[sortColumn];
        let valB = b[sortColumn];

        if (valA === undefined || valA === null) valA = '';
        if (valB === undefined || valB === null) valB = '';

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return processed || [];
  }, [reportData, activeReport, searchQuery, sortColumn, sortDirection, getSummaryData]);

  const processedData = getProcessedData() || [];

  const renderSortableHeader = (columnKey: string, label: string, align: 'left' | 'center' | 'right' = 'left') => {
    const isSorted = sortColumn === columnKey;
    return (
      <th 
        onClick={() => handleSort(columnKey)}
        className={`px-3 sm:px-4 py-3 text-${align} text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition select-none whitespace-nowrap`}
      >
        <div className={`flex items-center space-x-1 ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start'}`}>
          <span>{label}</span>
          <span className="text-gray-400">
            {isSorted ? (
              sortDirection === 'asc' ? <ArrowUp size={14} className="text-blue-600 font-bold" /> : <ArrowDown size={14} className="text-blue-600 font-bold" />
            ) : (
              <ArrowUpDown size={12} className="opacity-40 hover:opacity-100" />
            )}
          </span>
        </div>
      </th>
    );
  };

  const loadReportData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      let result;
      
      switch (activeReport) {
        case 'member-wise':
          result = await AdminService.getMemberWiseReport(filters);
          break;
        case 'meeting-wise':
          result = await AdminService.getMeetingWiseReport(filters);
          break;
        case 'summary':
          result = await AdminService.getSummaryReport(filters);
          break;
        case 'member-search':
          result = await AdminService.getMemberSearchReport(filters);
          break;
        default:
          result = { data: [], error: null };
      }

      if (result.data) {
        setReportData(result.data);
      } else if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [activeReport, filters]);

  useEffect(() => {
    if (activeReport) {
      loadReportData();
    }
  }, [activeReport, filters, loadReportData]);

  const getReportHeadersAndData = () => {
    let headers: string[] = [];
    let data: any[] = [];

    switch (activeReport) {
      case 'member-wise':
        headers = ['Member Name', 'Phone', 'Meeting Type', 'Total Meetings', 'Present', 'Absent', 'Attendance %'];
        if (dateDetailMode === 'present') headers.push('Dates of Attendance');
        else if (dateDetailMode === 'absent') headers.push('Dates of Absence');
        else if (dateDetailMode === 'both') headers.push('Dates of Attendance', 'Dates of Absence');

        data = processedData.map((row: any) => {
          const r = [row.name, row.phone, row.meetingType, row.total, row.present, row.absent, `${row.percentage.toFixed(1)}%`];
          if (dateDetailMode === 'present') r.push(row.presentDates?.join(', ') || 'None');
          else if (dateDetailMode === 'absent') r.push(row.absentDates?.join(', ') || 'None');
          else if (dateDetailMode === 'both') {
            r.push(row.presentDates?.join(', ') || 'None');
            r.push(row.absentDates?.join(', ') || 'None');
          }
          return r;
        });
        break;
      case 'meeting-wise':
        headers = ['Meeting Date', 'Meeting Type', 'Convener', 'Total Members', 'Present', 'Absent', 'Attendance %'];
        data = processedData.map((row: any) => [
          row.date, row.meetingType, row.convener, row.total, row.present, row.absent, `${row.percentage.toFixed(1)}%`
        ]);
        break;
      case 'summary':
        headers = ['Meeting Type', 'Total Meetings', 'Total Members', 'Total Present', 'Total Absent', 'Avg Attendance %'];
        data = processedData.map((row: any) => [
          row.meetingType, row.totalMeetings, row.totalMembers, row.totalPresent, row.totalAbsent, `${row.percentage.toFixed(1)}%`
        ]);
        break;
      case 'member-search':
        headers = ['Member Name', 'Phone', 'Total Meetings', 'Present', 'Absent', 'Attendance %', 'Meeting Types'];
        if (dateDetailMode === 'present') headers.push('Dates of Attendance');
        else if (dateDetailMode === 'absent') headers.push('Dates of Absence');
        else if (dateDetailMode === 'both') headers.push('Dates of Attendance', 'Dates of Absence');

        data = processedData.map((row: any) => {
          const r = [row.name, row.phone, row.total, row.present, row.absent, `${row.percentage.toFixed(1)}%`, row.meetingTypesStr];
          if (dateDetailMode === 'present') r.push(row.presentDates?.join(', ') || 'None');
          else if (dateDetailMode === 'absent') r.push(row.absentDates?.join(', ') || 'None');
          else if (dateDetailMode === 'both') {
            r.push(row.presentDates?.join(', ') || 'None');
            r.push(row.absentDates?.join(', ') || 'None');
          }
          return r;
        });
        break;
    }
    return { headers, data };
  };

  const exportToCSV = () => {
    if (processedData.length === 0) return;

    const { headers, data } = getReportHeadersAndData();
    const filterSummary = getActiveFiltersSummary();
    
    let csv = '';
    
    // Add Report Header
    csv += `"${activeReport.replace('-', ' ').toUpperCase()} REPORT"\n`;
    csv += `"Generated:","${new Date().toLocaleString()}"\n`;
    if (filterSummary.length > 0) {
      csv += `"Filters Applied:","${filterSummary.join(' | ')}"\n`;
    }
    csv += '\n';

    // Add Table
    csv += headers.map(h => `"${h}"`).join(',') + '\n';
    data.forEach(row => {
      csv += row.map((field: any) => `"${field}"`).join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SSF-Report-${activeReport}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    if (processedData.length === 0) return;

    const filterSummary = getActiveFiltersSummary();

    switch (activeReport) {
      case 'member-wise':
      case 'member-search':
        PDFReportGenerator.generateMemberWisePDF(processedData, {
          reportTitle: activeReport === 'member-search' ? 'Member Search Attendance Report' : 'Member Attendance Report',
          filterSummary,
          dateDetailMode
        });
        break;
      case 'meeting-wise':
        PDFReportGenerator.generateMeetingWisePDF(processedData, {
          reportTitle: 'Meeting Wise Attendance Report',
          filterSummary
        });
        break;
      case 'summary':
        PDFReportGenerator.generateSummaryPDF(processedData, {
          reportTitle: 'Attendance Summary Report',
          filterSummary
        });
        break;
    }
  };

  const formatReportForText = () => {
    if (processedData.length === 0) return '';

    const filterSummary = getActiveFiltersSummary();

    let text = `📊 *${activeReport.replace('-', ' ').toUpperCase()} REPORT*\n`;
    text += `📅 Generated: ${new Date().toLocaleString()}\n`;
    
    if (filterSummary.length > 0) {
      text += `\n🎯 *APPLIED FILTERS:*\n`;
      filterSummary.forEach(f => text += `• ${f}\n`);
    }

    if ((activeReport === 'member-wise' || activeReport === 'member-search') && dateDetailMode !== 'none') {
      const modeLabel = dateDetailMode === 'present' ? 'Dates of Attendance' : dateDetailMode === 'absent' ? 'Dates of Absence' : 'Present & Absent Dates';
      text += `📌 *Date View Mode:* ${modeLabel}\n`;
    }

    text += `\n━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    // Add data rows in a list format suitable for WhatsApp
    processedData.forEach((item: any) => {
      if (activeReport === 'member-wise' || activeReport === 'member-search') {
        text += `🔹 *${item.name}* (${item.phone})\n`;
        if (item.meetingType) text += `   🏷️ Meeting Type: ${item.meetingType}\n`;
        text += `   📊 Meetings: Attended ${item.present}/${item.total} (${item.percentage.toFixed(1)}%)\n`;

        if ((dateDetailMode === 'present' || dateDetailMode === 'both') && item.presentDates?.length > 0) {
          text += `   ✅ *Dates Present:* ${item.presentDates.join(', ')}\n`;
        }
        if ((dateDetailMode === 'absent' || dateDetailMode === 'both') && item.absentDates?.length > 0) {
          text += `   ❌ *Dates Absent:* ${item.absentDates.join(', ')}\n`;
        }
      } else if (activeReport === 'meeting-wise') {
        text += `🔹 *${item.meetingType}* (${item.date})\n`;
        if (item.convener) text += `   👤 Convener: ${item.convener}\n`;
        text += `   👥 Members: Total ${item.total} | ✅ Present ${item.present} | ❌ Absent ${item.absent} (${item.percentage.toFixed(1)}%)\n`;
      } else if (activeReport === 'summary') {
        text += `🔹 *${item.meetingType}*\n`;
        text += `   📊 Total Meetings: ${item.totalMeetings} | Total Members: ${item.totalMembers}\n`;
        text += `   📈 Avg Attendance: ${item.percentage.toFixed(1)}% (Present: ${item.totalPresent}, Absent: ${item.totalAbsent})\n`;
      }
      text += '\n';
    });

    text += `\n━━━━━━━━━━━━━━━━━━━━━━\n`;
    text += `📱 *State Meeting Tracker | SSF Kerala*`;
    
    return text;
  };

  const copyToClipboard = async () => {
    const formattedText = formatReportForText();
    if (!formattedText) return;

    try {
      await navigator.clipboard.writeText(formattedText);
      alert('Report copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = formattedText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Report copied to clipboard!');
    }
  };

  const shareViaWhatsApp = () => {
    const formattedText = formatReportForText();
    if (!formattedText) return;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(formattedText)}`;
    window.open(whatsappUrl, '_blank');
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const updateFilter = (key: string, value: string) => {
    setFilters((prev: any) => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  return (
    <div className="space-y-6">
      {/* Responsive Horizontal Report Type Tabs */}
      <div className="overflow-x-auto no-scrollbar scroll-smooth pb-1">
        <div className="flex space-x-2.5 min-w-max">
          <button
            onClick={() => setActiveReport('member-wise')}
            className={`flex items-center space-x-2.5 px-4 py-3 border rounded-xl transition text-left ${
              activeReport === 'member-wise'
                ? 'border-blue-600 bg-blue-50/80 text-blue-900 shadow-xs'
                : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
            }`}
          >
            <Users className="text-blue-600 shrink-0" size={18} />
            <div>
              <p className="font-bold text-xs sm:text-sm">Member Wise</p>
              <p className="text-[10px] text-gray-500">Attendance by member</p>
            </div>
          </button>

          <button
            onClick={() => setActiveReport('meeting-wise')}
            className={`flex items-center space-x-2.5 px-4 py-3 border rounded-xl transition text-left ${
              activeReport === 'meeting-wise'
                ? 'border-green-600 bg-green-50/80 text-green-900 shadow-xs'
                : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
            }`}
          >
            <Calendar className="text-green-600 shrink-0" size={18} />
            <div>
              <p className="font-bold text-xs sm:text-sm">Meeting Wise</p>
              <p className="text-[10px] text-gray-500">Attendance by meeting</p>
            </div>
          </button>

          <button
            onClick={() => setActiveReport('summary')}
            className={`flex items-center space-x-2.5 px-4 py-3 border rounded-xl transition text-left ${
              activeReport === 'summary'
                ? 'border-purple-600 bg-purple-50/80 text-purple-900 shadow-xs'
                : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
            }`}
          >
            <TrendingUp className="text-purple-600 shrink-0" size={18} />
            <div>
              <p className="font-bold text-xs sm:text-sm">Summary Report</p>
              <p className="text-[10px] text-gray-500">Overall statistics</p>
            </div>
          </button>

          <button
            onClick={() => setActiveReport('member-search')}
            className={`flex items-center space-x-2.5 px-4 py-3 border rounded-xl transition text-left ${
              activeReport === 'member-search'
                ? 'border-orange-600 bg-orange-50/80 text-orange-900 shadow-xs'
                : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
            }`}
          >
            <Search className="text-orange-600 shrink-0" size={18} />
            <div>
              <p className="font-bold text-xs sm:text-sm">Member Search</p>
              <p className="text-[10px] text-gray-500">Find member attendance</p>
            </div>
          </button>
        </div>
      </div>

      {/* Search Bar for Member Search */}
      {activeReport === 'member-search' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or phone number..."
                className="w-full pl-9 pr-3 sm:pl-10 sm:pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
              />
            </div>
            <button
              onClick={clearFilters}
              className="px-3.5 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition text-xs sm:text-sm min-h-[42px]"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      {activeReport !== 'member-search' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center space-x-2">
              <Filter size={18} className="text-gray-600" />
              <h3 className="text-sm sm:text-base font-bold text-gray-900">Filters</h3>
              {Object.keys(filters).filter(k => filters[k]).length > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold bg-blue-100 text-blue-700 rounded-full">
                  {Object.keys(filters).filter(k => filters[k]).length} active
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-3 py-1.5 text-xs font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              {Object.keys(filters).some(k => filters[k]) && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 text-xs font-semibold border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pt-2 border-t border-gray-100">
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Search Report Results
                </label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by meeting type, convener, member name, phone, date..."
                    className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Meeting Type
                </label>
                <select
                  value={filters.meeting_type_id || ''}
                  onChange={(e) => updateFilter('meeting_type_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                >
                  <option value="">All Types</option>
                  {meetingTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} - {type.level}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.start_date || ''}
                  onChange={(e) => updateFilter('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.end_date || ''}
                  onChange={(e) => updateFilter('end_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Convener
                </label>
                <select
                  value={filters.convener_id || ''}
                  onChange={(e) => updateFilter('convener_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                >
                  <option value="">All Conveners</option>
                  {users.filter(u => !u.is_admin).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} - {user.phone}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Report Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center space-x-2">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 capitalize">
              {activeReport === 'member-search' ? 'Member Search Results' : `${activeReport.replace('-', ' ')} Report`}
            </h3>
            <span className="px-2.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full font-bold">
              {processedData.length} records
            </span>
          </div>

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2">
            <button
              onClick={exportToCSV}
              disabled={loading || processedData.length === 0}
              className="flex items-center justify-center space-x-1.5 px-3 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 text-xs font-semibold transition disabled:opacity-50 min-h-[38px] shadow-2xs"
            >
              <Download size={14} />
              <span>CSV</span>
            </button>
            <button
              onClick={exportToPDF}
              disabled={loading || processedData.length === 0}
              className="flex items-center justify-center space-x-1.5 px-3 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 text-xs font-semibold transition disabled:opacity-50 min-h-[38px] shadow-2xs"
            >
              <FileText size={14} />
              <span>PDF</span>
            </button>
            <button
              onClick={copyToClipboard}
              disabled={loading || processedData.length === 0}
              className="flex items-center justify-center space-x-1.5 px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-xs font-semibold transition disabled:opacity-50 min-h-[38px] shadow-2xs"
              title="Copy formatted report to clipboard"
            >
              <Copy size={14} />
              <span>Copy</span>
            </button>
            <button
              onClick={shareViaWhatsApp}
              disabled={loading || processedData.length === 0}
              className="flex items-center justify-center space-x-1.5 px-3 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-xs font-semibold transition disabled:opacity-50 min-h-[38px] shadow-2xs"
              title="Share via WhatsApp"
            >
              <MessageSquare size={14} />
              <span>WhatsApp</span>
            </button>
          </div>
        </div>

        {/* Date Detail Mode Selector (Only for Member Wise / Member Search) */}
        {(activeReport === 'member-wise' || activeReport === 'member-search') && (
          <div className="flex flex-wrap items-center gap-2 pt-1 pb-2 border-b border-gray-100">
            <span className="text-xs font-bold text-gray-700 mr-1">Date Details Option:</span>
            <button
              onClick={() => setDateDetailMode('none')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                dateDetailMode === 'none'
                  ? 'bg-blue-600 text-white shadow-2xs font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Summary Only
            </button>
            <button
              onClick={() => setDateDetailMode('present')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center space-x-1 ${
                dateDetailMode === 'present'
                  ? 'bg-green-600 text-white shadow-2xs font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CheckCircle2 size={13} />
              <span>Dates of Attendance</span>
            </button>
            <button
              onClick={() => setDateDetailMode('absent')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center space-x-1 ${
                dateDetailMode === 'absent'
                  ? 'bg-red-600 text-white shadow-2xs font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <XCircle size={13} />
              <span>Dates of Absence</span>
            </button>
            <button
              onClick={() => setDateDetailMode('both')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                dateDetailMode === 'both'
                  ? 'bg-purple-600 text-white shadow-2xs font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Both Present & Absent
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-xs sm:text-sm mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <LoadingState title="Generating Report Analytics..." minHeight="min-h-[250px]" />
        ) : processedData.length > 0 ? (
          <div>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 max-w-full">
              <table className="w-full text-xs sm:text-sm divide-y divide-gray-200 min-w-max">
                <thead className="bg-gray-50">
                  <tr>
                    {activeReport === 'member-wise' && (
                      <>
                        {renderSortableHeader('name', 'Member Name')}
                        {renderSortableHeader('phone', 'Phone')}
                        {renderSortableHeader('meetingType', 'Meeting Type')}
                        {renderSortableHeader('total', 'Total', 'center')}
                        {renderSortableHeader('present', 'Present', 'center')}
                        {renderSortableHeader('absent', 'Absent', 'center')}
                        {renderSortableHeader('percentage', 'Attendance %', 'center')}
                        {(dateDetailMode === 'present' || dateDetailMode === 'both') && (
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Dates of Attendance</th>
                        )}
                        {(dateDetailMode === 'absent' || dateDetailMode === 'both') && (
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Dates of Absence</th>
                        )}
                      </>
                    )}
                    {activeReport === 'meeting-wise' && (
                      <>
                        {renderSortableHeader('date', 'Date')}
                        {renderSortableHeader('meetingType', 'Meeting Type')}
                        {renderSortableHeader('convener', 'Convener')}
                        {renderSortableHeader('total', 'Total', 'center')}
                        {renderSortableHeader('present', 'Present', 'center')}
                        {renderSortableHeader('absent', 'Absent', 'center')}
                        {renderSortableHeader('percentage', 'Attendance %', 'center')}
                      </>
                    )}
                    {activeReport === 'summary' && (
                      <>
                        {renderSortableHeader('meetingType', 'Meeting Type')}
                        {renderSortableHeader('totalMeetings', 'Total Meetings', 'center')}
                        {renderSortableHeader('totalMembers', 'Total Members', 'center')}
                        {renderSortableHeader('totalPresent', 'Total Present', 'center')}
                        {renderSortableHeader('totalAbsent', 'Total Absent', 'center')}
                        {renderSortableHeader('percentage', 'Avg Attendance %', 'center')}
                      </>
                    )}
                    {activeReport === 'member-search' && (
                      <>
                        {renderSortableHeader('name', 'Member Name')}
                        {renderSortableHeader('phone', 'Phone')}
                        {renderSortableHeader('total', 'Total Meetings', 'center')}
                        {renderSortableHeader('present', 'Present', 'center')}
                        {renderSortableHeader('absent', 'Absent', 'center')}
                        {renderSortableHeader('percentage', 'Attendance %', 'center')}
                        {renderSortableHeader('meetingTypesStr', 'Meeting Types')}
                        {(dateDetailMode === 'present' || dateDetailMode === 'both') && (
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Dates of Attendance</th>
                        )}
                        {(dateDetailMode === 'absent' || dateDetailMode === 'both') && (
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Dates of Absence</th>
                        )}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeReport === 'member-wise' && processedData.map((stats: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-gray-900 whitespace-nowrap">{stats.name}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-gray-600 whitespace-nowrap">{stats.phone}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-gray-700 whitespace-nowrap">{stats.meetingType}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-gray-900 font-medium">{stats.total}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-green-600 font-semibold">{stats.present}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-red-600 font-semibold">{stats.absent}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center font-medium">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          stats.percentage >= 75
                            ? 'bg-green-100 text-green-800'
                            : stats.percentage >= 50
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {stats.percentage.toFixed(1)}%
                        </span>
                      </td>
                      {(dateDetailMode === 'present' || dateDetailMode === 'both') && (
                        <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs text-green-700 max-w-xs">
                          {stats.presentDates && stats.presentDates.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {stats.presentDates.map((d: string, idx: number) => (
                                <span key={idx} className="bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded text-[11px]">
                                  {d}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">None</span>
                          )}
                        </td>
                      )}
                      {(dateDetailMode === 'absent' || dateDetailMode === 'both') && (
                        <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs text-red-700 max-w-xs">
                          {stats.absentDates && stats.absentDates.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {stats.absentDates.map((d: string, idx: number) => (
                                <span key={idx} className="bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded text-[11px]">
                                  {d}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">None</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {activeReport === 'meeting-wise' && processedData.map((stats: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-gray-700 whitespace-nowrap">{stats.date}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-gray-900 whitespace-nowrap">{stats.meetingType}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-gray-700 whitespace-nowrap">{stats.convener}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-gray-900 font-medium">{stats.total}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-green-600 font-semibold">{stats.present}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-red-600 font-semibold">{stats.absent}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center font-medium">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          stats.percentage >= 75
                            ? 'bg-green-100 text-green-800'
                            : stats.percentage >= 50
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {stats.percentage.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  {activeReport === 'summary' && processedData.map((stats: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-gray-900 whitespace-nowrap">{stats.meetingType}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-gray-900 font-medium">{stats.totalMeetings}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-gray-700">{stats.totalMembers}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-green-600 font-semibold">{stats.totalPresent}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-red-600 font-semibold">{stats.totalAbsent}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center font-medium">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          stats.percentage >= 75
                            ? 'bg-green-100 text-green-800'
                            : stats.percentage >= 50
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {stats.percentage.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  {activeReport === 'member-search' && processedData.map((stats: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-gray-900 whitespace-nowrap">{stats.name}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-gray-600 whitespace-nowrap">{stats.phone}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-gray-900 font-medium">{stats.total}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-green-600 font-semibold">{stats.present}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center text-red-600 font-semibold">{stats.absent}</td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-center font-medium">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          stats.percentage >= 75
                            ? 'bg-green-100 text-green-800'
                            : stats.percentage >= 50
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {stats.percentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs text-gray-500 max-w-xs truncate">
                        {stats.meetingTypesStr}
                      </td>
                      {(dateDetailMode === 'present' || dateDetailMode === 'both') && (
                        <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs text-green-700 max-w-xs">
                          {stats.presentDates && stats.presentDates.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {stats.presentDates.map((d: string, idx: number) => (
                                <span key={idx} className="bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded text-[11px]">
                                  {d}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">None</span>
                          )}
                        </td>
                      )}
                      {(dateDetailMode === 'absent' || dateDetailMode === 'both') && (
                        <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs text-red-700 max-w-xs">
                          {stats.absentDates && stats.absentDates.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {stats.absentDates.map((d: string, idx: number) => (
                                <span key={idx} className="bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded text-[11px]">
                                  {d}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">None</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {activeReport === 'member-wise' && processedData.map((stats: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3.5 bg-white text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900 text-sm">{stats.name}</span>
                    <span className={`px-2 py-0.5 font-bold rounded-full ${
                      stats.percentage >= 75 ? 'bg-green-100 text-green-800' : stats.percentage >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {stats.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-gray-500">{stats.phone} | {stats.meetingType}</div>
                  <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-gray-100">
                    <div className="bg-gray-50 p-1.5 rounded"><div className="text-gray-400">Total</div><div className="font-medium text-gray-900">{stats.total}</div></div>
                    <div className="bg-green-50 p-1.5 rounded"><div className="text-green-600">Present</div><div className="font-semibold text-green-700">{stats.present}</div></div>
                    <div className="bg-red-50 p-1.5 rounded"><div className="text-red-600">Absent</div><div className="font-semibold text-red-700">{stats.absent}</div></div>
                  </div>

                  {(dateDetailMode === 'present' || dateDetailMode === 'both') && stats.presentDates?.length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="font-semibold text-green-700 mb-1 flex items-center space-x-1">
                        <CheckCircle2 size={12} />
                        <span>Dates Attended:</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {stats.presentDates.map((d: string, idx: number) => (
                          <span key={idx} className="bg-green-50 text-green-800 border border-green-200 px-1.5 py-0.5 rounded text-[10px]">
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(dateDetailMode === 'absent' || dateDetailMode === 'both') && stats.absentDates?.length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="font-semibold text-red-700 mb-1 flex items-center space-x-1">
                        <XCircle size={12} />
                        <span>Dates Absent:</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {stats.absentDates.map((d: string, idx: number) => (
                          <span key={idx} className="bg-red-50 text-red-800 border border-red-200 px-1.5 py-0.5 rounded text-[10px]">
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {activeReport === 'meeting-wise' && processedData.map((stats: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3.5 bg-white text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900 text-sm">{stats.meetingType}</span>
                    <span className={`px-2 py-0.5 font-bold rounded-full ${
                      stats.percentage >= 75 ? 'bg-green-100 text-green-800' : stats.percentage >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {stats.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-gray-600">Date: {stats.date} | Convener: {stats.convener}</div>
                  <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-gray-100">
                    <div className="bg-gray-50 p-1.5 rounded"><div className="text-gray-400">Total</div><div className="font-medium text-gray-900">{stats.total}</div></div>
                    <div className="bg-green-50 p-1.5 rounded"><div className="text-green-600">Present</div><div className="font-semibold text-green-700">{stats.present}</div></div>
                    <div className="bg-red-50 p-1.5 rounded"><div className="text-red-600">Absent</div><div className="font-semibold text-red-700">{stats.absent}</div></div>
                  </div>
                </div>
              ))}

              {activeReport === 'summary' && processedData.map((stats: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3.5 bg-white text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900 text-sm">{stats.meetingType}</span>
                    <span className={`px-2 py-0.5 font-bold rounded-full ${
                      stats.percentage >= 75 ? 'bg-green-100 text-green-800' : stats.percentage >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {stats.percentage.toFixed(1)}% Avg
                    </span>
                  </div>
                  <div className="text-gray-500">Meetings: {stats.totalMeetings} | Total Members: {stats.totalMembers}</div>
                  <div className="grid grid-cols-2 gap-2 text-center pt-2 border-t border-gray-100">
                    <div className="bg-green-50 p-1.5 rounded"><div className="text-green-600">Total Present</div><div className="font-semibold text-green-700">{stats.totalPresent}</div></div>
                    <div className="bg-red-50 p-1.5 rounded"><div className="text-red-600">Total Absent</div><div className="font-semibold text-red-700">{stats.totalAbsent}</div></div>
                  </div>
                </div>
              ))}

              {activeReport === 'member-search' && processedData.map((stats: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3.5 bg-white text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900 text-sm">{stats.name}</span>
                    <span className={`px-2 py-0.5 font-bold rounded-full ${
                      stats.percentage >= 75 ? 'bg-green-100 text-green-800' : stats.percentage >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {stats.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-gray-500">{stats.phone}</div>
                  {stats.meetingTypesStr && (
                    <div className="text-gray-500 italic text-3xs">Types: {stats.meetingTypesStr}</div>
                  )}
                  <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-gray-100">
                    <div className="bg-gray-50 p-1.5 rounded"><div className="text-gray-400">Total</div><div className="font-medium text-gray-900">{stats.total}</div></div>
                    <div className="bg-green-50 p-1.5 rounded"><div className="text-green-600">Present</div><div className="font-semibold text-green-700">{stats.present}</div></div>
                    <div className="bg-red-50 p-1.5 rounded"><div className="text-red-600">Absent</div><div className="font-semibold text-red-700">{stats.absent}</div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 text-xs sm:text-sm">
            {activeReport === 'member-search' && searchQuery 
              ? 'No members found matching your search.' 
              : 'No data found for the selected filters.'
            }
          </div>
        )}
      </div>
    </div>
  );
}
